import type {
  Customer,
  CustomerAccessTokenCreatePayload,
  CustomerAddressCreatePayload,
  CustomerAddressDeletePayload,
  CustomerAddressUpdatePayload,
  CustomerCreatePayload,
  CustomerDefaultAddressUpdatePayload,
  CustomerRecoverPayload,
  CustomerUpdatePayload,
  Metafield,
  MetafieldReferenceConnection,
  MetafieldReferenceEdge,
  Order,
  OrderConnection,
  OrderEdge,
  OrderLineItem,
  OrderLineItemConnection,
  OrderLineItemEdge,
  PageInfo,
  Product,
  ProductVariant,
  ProductVariantConnection,
  Maybe,
} from "@shopify/hydrogen-react/storefront-api-types";
import { mutation, query } from "gql-query-builder";

import {
  APP_NAME,
  customerAccessTokenFields,
  customerFields,
  customerUserErrorFields,
  fulfillmentFields,
  imageFields,
  mailingAddressFields,
  moneyFields,
  orderAmountFields,
  orderFields,
  pageFields,
  productFields,
  selectedOptionFields,
  userErrorFields,
} from "./fields";
import {
  birthMetafield,
  cardInfoMetafield,
  excludeLimitMetafield,
  genderMetafield,
  wishListMetafield,
} from "./metafields/customer-metafields";
import {
  drawStatusMetafield,
  featuredImageMetafield,
  instagramMetafield,
  isFamilySaleMetafield,
  isOfflineMetafield,
  returnStatusMetafield,
} from "./metafields/order-metafields";
import type { IProductMetafields } from "./product-gql";
import { customerProductMetafields } from "./product-gql";
import { moneyFragment } from "./fragments";

export const addresses = {
  operation: "addresses(first: 20)",
  fields: [{ edges: [{ node: mailingAddressFields }] }],
  variables: {},
};

export const productVariantFields = [
  "availableForSale",
  "barcode",
  { compareAtPrice: moneyFields },
  "currentlyNotInStock",
  "id",
  { image: imageFields },
  { price: moneyFields },
  { product: [...productFields, ...customerProductMetafields[APP_NAME]] },
  "quantityAvailable",
  { selectedOptions: selectedOptionFields },
  "sku",
  "weight",
  "weightUnit",
  "title",
];

export const lineItems = {
  operation: "lineItems(first: 100)",
  fields: [
    {
      edges: [
        {
          node: [
            {
              variant: [
                {
                  operation: "ProductVariant",
                  fields: productVariantFields,
                  fragment: true,
                },
              ],
              discountAllocations: [
                {
                  allocatedAmount: moneyFields,
                  discountApplication: [
                    {
                      operation: "ManualDiscountApplication",
                      fields: ["title", "description"],
                      fragment: true,
                    },
                    {
                      operation: "DiscountCodeApplication",
                      fields: ["code", "applicable"],
                      fragment: true,
                    },
                  ],
                },
              ],
            },
            "title",
            "quantity",
            "currentQuantity",
            { discountedTotalPrice: moneyFields },
            { originalTotalPrice: moneyFields },
          ],
        },
      ],
    },
  ],
  variables: {},
};

export const discountApplications = {
  operation: "discountApplications(first: 3)",
  fields: [
    {
      edges: [
        {
          node: [
            {
              "... on ManualDiscountApplication": ["title", "description"],
              "... on DiscountCodeApplication": ["code", "applicable"],
              value: [moneyFragment], // 필드가 어떤 타입이든지 상관없이 moneyFragment에 정의된 필드 가져옴
            },
          ],
        },
      ],
    },
  ],
  variables: {},
};

export const successfulFulfillments = {
  operation: "successfulFulfillments(first: 10)",
  fields: [
    ...fulfillmentFields,
    {
      operation: "fulfillmentLineItems(first: 100)",
      fields: [{ edges: [{ node: [{ lineItem: ["title", { variant: [{ product: ["vendor"] }] }] }] }] }],
      variables: {},
    },
  ],
  variables: {},
};

export const orders = {
  operation: "orders(first: 250, sortKey: PROCESSED_AT, reverse: true)",
  fields: [
    {
      edges: [
        {
          node: [
            ...orderFields,
            lineItems,
            successfulFulfillments,
            drawStatusMetafield,
            featuredImageMetafield,
            isOfflineMetafield,
            returnStatusMetafield,
            instagramMetafield,
            cardInfoMetafield,
            excludeLimitMetafield,
            discountApplications,
            isFamilySaleMetafield,
          ],
        },
      ],
    },
  ],
  variables: {},
};

/**
 * 고객 생성 (회원가입)
 */
export interface ICustomerCreateMutation {
  customerCreate: CustomerCreatePayload;
}

export const customerCreateMutation = mutation(
  {
    operation: "customerCreate",
    fields: [
      { customer: customerFields },
      { customerUserErrors: customerUserErrorFields },
      { userErrors: userErrorFields },
    ],
    variables: { input: { type: "CustomerCreateInput!" } },
  },
  undefined,
  { operationName: "CustomerCreate" },
);

/**
 * 고객 토큰 생성 - 로그인 시 호출
 */
export interface ICustomerAccessTokenCreateMutation {
  customerAccessTokenCreate: CustomerAccessTokenCreatePayload;
}

export const customerAccessTokenCreateMutation = mutation(
  {
    operation: "customerAccessTokenCreate",
    fields: [{ customerAccessToken: customerAccessTokenFields }, { customerUserErrors: customerUserErrorFields }],
    variables: { input: { type: "CustomerAccessTokenCreateInput!" } },
  },
  undefined,
  { operationName: "CustomerAccessTokenCreate" },
);

/**
 * 비밀번호 재설정 - 이메일 전송
 */
export interface ICustomerRecoverMutation {
  customerRecover: CustomerRecoverPayload;
}

export const customerRecoverMutation = mutation(
  {
    operation: "customerRecover",
    fields: [{ customerUserErrors: customerUserErrorFields }, { userErrors: userErrorFields }],
    variables: { email: { type: "String!" } },
  },
  undefined,
  { operationName: "CustomerRecover" },
);

/**
 * 고객 배송지 생성
 */
export interface ICustomerAddressCreateMutation {
  customerAddressCreate: CustomerAddressCreatePayload;
}

export const customerAddressCreateMutation = mutation(
  {
    operation: "customerAddressCreate",
    fields: [
      { customerAddress: mailingAddressFields },
      { customerUserErrors: customerUserErrorFields },
      { userErrors: userErrorFields },
    ],
    variables: {
      address: { type: "MailingAddressInput!" },
      customerAccessToken: { type: "String!" },
    },
  },
  undefined,
  { operationName: "CustomerAddressCreate" },
);

/**
 * 고객 배송지 삭제
 */
export interface ICustomerAddressDeleteMutation {
  customerAddressDelete: CustomerAddressDeletePayload;
}

export const customerAddressDeleteMutation = mutation(
  {
    operation: "customerAddressDelete",
    fields: [
      "deletedCustomerAddressId",
      { customerUserErrors: customerUserErrorFields },
      { userErrors: userErrorFields },
    ],
    variables: {
      customerAccessToken: { type: "String!" },
      id: { type: "ID!" },
    },
  },
  undefined,
  { operationName: "CustomerAddressDelete" },
);

/**
 * 고객 배송지 수정
 */
export interface ICustomerAddressUpdateMutation {
  customerAddressUpdate: CustomerAddressUpdatePayload;
}

export const customerAddressUpdateMutation = mutation(
  {
    operation: "customerAddressUpdate",
    fields: [
      { customerAddress: mailingAddressFields },
      { customerUserErrors: customerUserErrorFields },
      { userErrors: userErrorFields },
    ],
    variables: {
      address: { type: "MailingAddressInput!" },
      customerAccessToken: { type: "String!" },
      id: { type: "ID!" },
    },
  },
  undefined,
  { operationName: "CustomerAddressUpdate" },
);

/**
 * 고객 배송지의 기본배송지 설정
 */
export interface ICustomerDefaultAddressUpdateMutation {
  customerDefaultAddressUpdate: CustomerDefaultAddressUpdatePayload;
}

export const customerDefaultAddressUpdateMutation = mutation(
  {
    operation: "customerDefaultAddressUpdate",
    fields: [
      { customer: customerFields },
      { customerUserErrors: customerUserErrorFields },
      { userErrors: userErrorFields },
    ],
    variables: {
      addressId: { type: "ID!" },
      customerAccessToken: { type: "String!" },
    },
  },
  undefined,
  { operationName: "CustomerDefaultAddressUpdate" },
);

export interface IOrdersLineItemsEdges extends OrderLineItemEdge {
  node: OrderLineItem & {
    returnReason?: string;
    index?: number;
    variant: Maybe<ProductVariant & { product: Product & IProductMetafields }> | null;
  };
}

/**
 * 로그인시 생성된 고객 토큰으로 고객 정보 조회
 */
export interface IOrdersEdgesNode extends Order {
  drawStatus?: Metafield;
  featuredImage?: Metafield;
  instagram?: Metafield;
  returnStatus?: Metafield;
  isOffline?: Metafield;
  exclude_limit?: Metafield;
  cardInfo?: Metafield;
  isfamilysale?: Metafield;
  lineItems: OrderLineItemConnection & {
    edges: IOrdersLineItemsEdges[];
  };
}

export interface IOrdersEdges extends OrderConnection {
  edges: (OrderEdge & { node: IOrdersEdgesNode })[];
}

export interface IWishlistReference extends MetafieldReferenceConnection {
  edges: (MetafieldReferenceEdge & IWishlistNode)[];
}

export interface IWishlistNode extends MetafieldReferenceEdge {
  node: Product & {
    index: number;
    launch?: Metafield;
    variants: ProductVariantConnection;
  };
}

export interface ICustomerQuery {
  customer: Customer & {
    orders: IOrdersEdges;
    gender?: Metafield;
    birth?: Metafield;
    wishlist?: Metafield & {
      references: (MetafieldReferenceConnection & {
        edges: IWishlistReference;
      })[];
    };
  };
}

export const customerQuery = query(
  {
    operation: "customer",
    fields: [
      ...customerFields,
      { defaultAddress: mailingAddressFields },
      addresses,
      orders,
      genderMetafield,
      birthMetafield,
      wishListMetafield,
    ],
    variables: { customerAccessToken: { type: "String!" } },
  },
  null,
  { operationName: "Customer" },
);

/**
 * 고객 정보 수정
 */
export interface ICustomerUpdateMutation {
  customerUpdate: CustomerUpdatePayload & {
    customer: ICustomerQuery["customer"];
  };
}

export const customerUpdateMutation = mutation(
  {
    operation: "customerUpdate",
    fields: [
      {
        customer: [
          ...customerFields,
          { defaultAddress: mailingAddressFields },
          addresses,
          orders,
          genderMetafield,
          birthMetafield,
          wishListMetafield,
        ],
      },
      { customerAccessToken: customerAccessTokenFields },
      { customerUserErrors: customerUserErrorFields },
      { userErrors: userErrorFields },
    ],
    variables: {
      customer: { type: "CustomerUpdateInput!" },
      customerAccessToken: { type: "String!" },
    },
  },
  undefined,
  { operationName: "CustomerUpdate" },
);

/**
 * 고객 주문 목록 조회
 */
export interface ICustomerOrdersQuery {
  customer: Customer & {
    email: string;
    orders: OrderConnection & {
      edges: (OrderEdge & { node: IOrdersEdgesNode })[];
      pageInfo: PageInfo;
    };
  };
}

export const customerOrdersQuery = query(
  {
    operation: "customer",
    fields: [
      {
        operation: "orders",
        fields: [
          {
            edges: [
              {
                node: [
                  ...orderFields,
                  lineItems,
                  successfulFulfillments,
                  drawStatusMetafield,
                  isOfflineMetafield,
                  featuredImageMetafield,
                  returnStatusMetafield,
                  instagramMetafield,
                  cardInfoMetafield,
                  excludeLimitMetafield,
                  discountApplications,
                  isFamilySaleMetafield,
                ],
              },
            ],
          },
          { pageInfo: pageFields },
        ],
        variables: {
          first: { type: "Int" },
          sortKey: { type: "OrderSortKeys" },
          reverse: { type: "Boolean" },
          after: { type: "String" },
        },
      },
    ],
    variables: { customerAccessToken: { type: "String!" } },
  },
  null,
  { operationName: "CustomerOrder" },
);

export const totalPaidAmountQuery = query(
  {
    operation: "customer",
    fields: [
      "email",
      {
        operation: "orders",
        fields: [
          {
            edges: [
              {
                node: [...orderAmountFields],
              },
            ],
          },
          { pageInfo: pageFields },
        ],
        variables: {
          first: { type: "Int" },
          sortKey: { type: "OrderSortKeys" },
          reverse: { type: "Boolean" },
          after: { type: "String" },
          query: { type: "String" },
        },
      },
    ],
    variables: { customerAccessToken: { type: "String!" } },
  },
  null,
  { operationName: "CustomerOrder" },
);
